import { z } from "zod";

// ---------------------------------------------------------------------------
// Feedback add-on contracts (see plans/feedback-addon-plan.md)
// ---------------------------------------------------------------------------

/** Per-dimension star rating, 0..5 (integer). 0 means "no rating given". */
export const feedbackStarSchema = z.number().int().min(0).max(5);

/** Comment max length applied across submit + display. */
export const FEEDBACK_COMMENT_MAX = 1000;

/** Fixed v1 choices for the customer acquisition follow-up. */
export const CUSTOMER_ACQUISITION_SOURCES = [
  "GOOGLE",
  "INSTAGRAM",
  "TIKTOK",
  "YELP",
  "FRIEND_OR_FAMILY",
  "WALK_IN",
  "OTHER",
  "PREFER_NOT_TO_SAY",
] as const;
export const customerAcquisitionSourceSchema = z.enum(
  CUSTOMER_ACQUISITION_SOURCES,
);
export type CustomerAcquisitionSource = z.infer<
  typeof customerAcquisitionSourceSchema
>;

export const CUSTOMER_ACQUISITION_OTHER_TEXT_MAX = 120;
export const CUSTOMER_ACQUISITION_RECENT_OTHER_LIMIT = 20;

// ── Public form (anonymous, token = appointmentId) ────────────────────────

/** Minimal data needed to render the feedback form on the public site. */
export const publicFeedbackFormResponseSchema = z.object({
  appointmentId: z.string(),
  salonId: z.string(),
  salonName: z.string(),
  serviceTitle: z.string().nullable(),
  memberName: z.string().nullable(),
  startsAt: z.string(), // ISO
  expiresAt: z.string(), // ISO
  expired: z.boolean(),
  alreadySubmitted: z.boolean(),
  needsAcquisitionAnswer: z.boolean(),
});
export type PublicFeedbackFormResponse = z.infer<
  typeof publicFeedbackFormResponseSchema
>;

export const publicFeedbackSubmitInputSchema = z.object({
  qualityRating: feedbackStarSchema,
  timeManagementRating: feedbackStarSchema,
  environmentRating: feedbackStarSchema,
  comment: z.string().max(FEEDBACK_COMMENT_MAX).optional(),
  /**
   * The comment may appear on the stylist's public Find profile. Absent from
   * a form that never showed that notice; the comment then stays private.
   */
  commentPublic: z.boolean().optional(),
});
export type PublicFeedbackSubmitInput = z.infer<
  typeof publicFeedbackSubmitInputSchema
>;

export const publicFeedbackSubmitResponseSchema = z.object({
  appointmentId: z.string(),
  alreadySubmitted: z.boolean().optional(),
  needsAcquisitionAnswer: z.boolean(),
});
export type PublicFeedbackSubmitResponse = z.infer<
  typeof publicFeedbackSubmitResponseSchema
>;

export const publicFeedbackAcquisitionInputSchema = z
  .object({
    source: customerAcquisitionSourceSchema,
    otherText: z
      .string()
      .trim()
      .min(1)
      .max(CUSTOMER_ACQUISITION_OTHER_TEXT_MAX)
      .nullable(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.source === "OTHER" && value.otherText === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["otherText"],
        message: "Tell us how you found the business",
      });
    }

    if (value.source !== "OTHER" && value.otherText !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["otherText"],
        message: "Other text is only allowed when source is OTHER",
      });
    }
  });
export type PublicFeedbackAcquisitionInput = z.infer<
  typeof publicFeedbackAcquisitionInputSchema
>;

export const publicFeedbackAcquisitionResponseSchema = z
  .object({
    appointmentId: z.string(),
    alreadyAnswered: z.boolean(),
  })
  .strict();
export type PublicFeedbackAcquisitionResponse = z.infer<
  typeof publicFeedbackAcquisitionResponseSchema
>;

// ── Dashboard (owner/member) ──────────────────────────────────────────────

export const feedbackListQuerySchema = z.object({
  from: z.string().optional(), // ISO date
  to: z.string().optional(), // ISO date
  teamMemberId: z.string().optional(),
  minRating: z.coerce.number().int().min(0).max(5).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type FeedbackListQuery = z.infer<typeof feedbackListQuerySchema>;

export const feedbackItemSchema = z.object({
  id: z.string(),
  appointmentId: z.string(),
  customerId: z.string().nullable(),
  customerName: z.string().nullable(),
  teamMemberId: z.string().nullable(),
  teamMemberName: z.string().nullable(),
  serviceTitle: z.string().nullable(),
  startsAt: z.string(), // ISO
  submittedAt: z.string(), // ISO
  qualityRating: feedbackStarSchema,
  timeManagementRating: feedbackStarSchema,
  environmentRating: feedbackStarSchema,
  averageRating: z.number().min(0).max(5),
  comment: z.string().nullable(),
});
export type FeedbackItem = z.infer<typeof feedbackItemSchema>;

export const feedbackListResponseSchema = z.object({
  items: z.array(feedbackItemSchema),
  total: z.number().int().min(0),
});
export type FeedbackListResponse = z.infer<typeof feedbackListResponseSchema>;

export const feedbackMemberBreakdownSchema = z.object({
  teamMemberId: z.string().nullable(),
  teamMemberName: z.string().nullable(),
  responses: z.number().int().min(0),
  averageQuality: z.number().min(0).max(5),
  averageTimeManagement: z.number().min(0).max(5),
  averageEnvironment: z.number().min(0).max(5),
  averageOverall: z.number().min(0).max(5),
});
export type FeedbackMemberBreakdown = z.infer<
  typeof feedbackMemberBreakdownSchema
>;

export const feedbackAcquisitionSourceBreakdownSchema = z.object({
  source: customerAcquisitionSourceSchema,
  count: z.number().int().min(0),
});
export type FeedbackAcquisitionSourceBreakdown = z.infer<
  typeof feedbackAcquisitionSourceBreakdownSchema
>;

export const feedbackAcquisitionRecentOtherSchema = z.object({
  text: z.string().min(1).max(CUSTOMER_ACQUISITION_OTHER_TEXT_MAX),
  answeredAt: z.string().datetime({ offset: true }),
});
export type FeedbackAcquisitionRecentOther = z.infer<
  typeof feedbackAcquisitionRecentOtherSchema
>;

export const feedbackAcquisitionSummarySchema = z
  .object({
    totalAnswers: z.number().int().min(0),
    bySource: z
      .array(feedbackAcquisitionSourceBreakdownSchema)
      .length(CUSTOMER_ACQUISITION_SOURCES.length)
      .superRefine((items, ctx) => {
        CUSTOMER_ACQUISITION_SOURCES.forEach((source, index) => {
          if (items[index]?.source !== source) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [index, "source"],
              message: `Expected ${source} at index ${index}`,
            });
          }
        });
      }),
    recentOther: z
      .array(feedbackAcquisitionRecentOtherSchema)
      .max(CUSTOMER_ACQUISITION_RECENT_OTHER_LIMIT),
  })
  .superRefine((value, ctx) => {
    const countedAnswers = value.bySource.reduce(
      (total, item) => total + item.count,
      0,
    );
    if (value.totalAnswers !== countedAnswers) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["totalAnswers"],
        message: "Total answers must equal the source counts",
      });
    }
  });
export type FeedbackAcquisitionSummary = z.infer<
  typeof feedbackAcquisitionSummarySchema
>;

export const feedbackSummaryDateBoundSchema = z.union([
  z.iso.date(),
  z.string().datetime({ offset: true }),
]);

export const feedbackSummaryQuerySchema = z
  .object({
    from: feedbackSummaryDateBoundSchema.optional(),
    to: feedbackSummaryDateBoundSchema.optional(),
  })
  .refine(
    ({ from, to }) => !from || !to || Date.parse(from) <= Date.parse(to),
    {
      path: ["to"],
      message: "To must be at or after from",
    },
  );
export type FeedbackSummaryQuery = z.infer<typeof feedbackSummaryQuerySchema>;

export const feedbackSummaryResponseSchema = z.object({
  totalResponses: z.number().int().min(0),
  // Feedback invites texted in range: `sentAt` set and in range. Invites that
  // never went out (no phone, no credits, still pending) are not counted.
  eligibleAppointments: z.number().int().min(0),
  // 0..1: the share of those invites whose appointment has feedback, whenever
  // it arrived. Direct-link answers without a sent invite are left out.
  responseRate: z.number().min(0).max(1),
  averageQuality: z.number().min(0).max(5),
  averageTimeManagement: z.number().min(0).max(5),
  averageEnvironment: z.number().min(0).max(5),
  averageOverall: z.number().min(0).max(5),
  byMember: z.array(feedbackMemberBreakdownSchema),
  acquisition: feedbackAcquisitionSummarySchema,
});
export type FeedbackSummaryResponse = z.infer<
  typeof feedbackSummaryResponseSchema
>;
